/**
 * When to ask someone again about installing the app.
 *
 * Split out of the prompt component because it's the only part with rules in
 * it, and rules that decide how often a person is interrupted deserve to be
 * checkable without a browser.
 *
 * The rule it replaces was "never ask twice": one dismissal wrote a permanent
 * flag. The funnel showed where that led — six of seven members reading the app
 * in an iPhone Safari tab, where Apple delivers no notifications at all, having
 * been asked exactly once.
 */

export const SNOOZE_KEY = 'pwa_snooze';
const DAY = 24 * 60 * 60 * 1000;

/**
 * How many days each successive "not now" buys.
 *
 * iOS is pressed harder because the cost of not installing is real there — no
 * reminder before picks lock, no result at the end of the week, ever. On
 * Android push works fine in the browser, so installing is a convenience and
 * being asked about it repeatedly would be pure nuisance.
 *
 * Running off the end of a list means they've said no that many times. At that
 * point the answer is settled and we stop.
 */
export const SNOOZE_DAYS = {
  ios:     [2, 4, 7, 14, 30],
  android: [7, 21, 60],
  other:   [7, 21, 60]
};

export function platformOf(ua, maxTouchPoints) {
  const s = ua || '';
  const isIos = /iPad|iPhone|iPod/.test(s) ||
    (/Macintosh/.test(s) && (maxTouchPoints || 0) > 1);
  if (isIos) return 'ios';
  if (/Android/.test(s)) return 'android';
  return 'other';
}

/** What the next dismissal should record. Pure — `now` is passed in. */
export function nextSnooze(count, platform, now) {
  const scale = SNOOZE_DAYS[platform] || SNOOZE_DAYS.other;
  const exhausted = count >= scale.length;
  return {
    count: count + 1,
    until: exhausted ? Number.MAX_SAFE_INTEGER : now + scale[Math.min(count, scale.length - 1)] * DAY,
    exhausted
  };
}

/** Are we still inside a snooze, and have they said no often enough to stop? */
export function snoozeState(stored, platform, now) {
  const s = stored || { count: 0, until: 0 };
  const scale = SNOOZE_DAYS[platform] || SNOOZE_DAYS.other;
  return {
    count: s.count || 0,
    snoozed: now < (s.until || 0),
    exhausted: (s.count || 0) >= scale.length
  };
}

/**
 * Read the stored snooze, migrating anyone still carrying the old permanent
 * flag. They're treated as having said no once — not as never to be asked
 * again, which is the behaviour being fixed.
 */
export function readSnooze(storage) {
  try {
    const raw = storage.getItem(SNOOZE_KEY);
    if (raw) return JSON.parse(raw);
    if (storage.getItem('pwa_dismissed')) return { count: 1, until: 0 };
  } catch (e) {}
  return { count: 0, until: 0 };
}
